import { useEffect, useMemo, useState } from "react";
import api from "../../lib/api";
import { fmt } from "../../lib/money";
import { Search, RotateCcw, Trash2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

/**
 * Site-wide deleted-orders log. Lets admins audit every deletion (who did it,
 * when, whether stock was restored) and restore any order in one click.
 */
export default function AdminDeletedOrders() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [adminMap, setAdminMap] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/deleted-orders?limit=500");
      setOrders(data.orders || []);
      // Best-effort: resolve admin user_ids → emails for the "deleted by" column
      const uniqAdminIds = Array.from(new Set((data.orders || []).map((o) => o.deleted_by).filter(Boolean)));
      if (uniqAdminIds.length > 0) {
        const map = {};
        await Promise.all(uniqAdminIds.map(async (id) => {
          try {
            const { data: d } = await api.get(`/admin/users/${encodeURIComponent(id)}/details`);
            map[id] = d?.user?.email || id;
          } catch { map[id] = id; }
        }));
        setAdminMap(map);
      }
    } catch {
      toast.error("Couldn't load deleted orders");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return orders;
    return orders.filter((o) => (
      (o.order_id || "").toLowerCase().includes(needle)
      || (o.user_id || "").toLowerCase().includes(needle)
      || (adminMap[o.deleted_by] || "").toLowerCase().includes(needle)
      || (o.tm_ref || "").toLowerCase().includes(needle)
      || (o.items || []).some((it) => (it.title || "").toLowerCase().includes(needle))
    ));
  }, [orders, q, adminMap]);

  const restore = async (o) => {
    const reDec = o.stock_restored
      ? window.confirm(`Restore #${o.order_id.slice(-8)}?\n\nOK = re-decrement stock (recommended: stock was restored on delete).\nCancel = leave stock unchanged.`)
      : false;
    if (!o.stock_restored) {
      const okay = window.confirm(`Restore #${o.order_id.slice(-8)}? (Stock will be left unchanged.)`);
      if (!okay) return;
    }
    try {
      await api.post(`/admin/deleted-orders/${o.order_id}/restore?re_decrement_stock=${reDec}`);
      toast.success("Order restored");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Restore failed");
    }
  };

  return (
    <div>
      <h1 className="font-display font-black text-4xl tracking-tight mb-2">Deleted orders</h1>
      <p className="text-sm text-neutral-600 mb-6">
        Every deletion across the site. Restore in one click — stock is handled automatically based on how the delete was performed.
      </p>

      <div className="relative max-w-md mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
        <input
          data-testid="deleted-orders-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search order id, user id, admin, product…"
          className="w-full h-10 pl-9 pr-3 border-2 border-black rounded-none text-sm"
        />
      </div>

      <div className="text-xs text-neutral-500 mb-3">
        {loading ? "Loading…" : `${filtered.length} of ${orders.length} deletion${orders.length === 1 ? "" : "s"}`}
      </div>

      {!loading && filtered.length === 0 && (
        <div className="border border-dashed border-neutral-300 p-8 text-center text-sm text-neutral-500 flex items-center justify-center gap-2">
          <Trash2 className="w-4 h-4" />
          {q ? "No deletions match." : "No deleted orders on file."}
        </div>
      )}

      <div className="space-y-3">
        {filtered.map((o) => (
          <div key={o.order_id} className="border-2 border-dashed border-[#FF2A00] bg-[#FFF5F2] p-3 sm:p-4" data-testid={`deleted-row-${o.order_id}`}>
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display font-bold text-base">#{o.order_id.slice(-8)}</span>
                  <span className="text-[10px] uppercase tracking-widest font-bold px-1.5 py-0.5 bg-[#FF2A00] text-white">DELETED</span>
                  {o.stock_restored && (
                    <span className="text-[10px] uppercase tracking-widest font-semibold px-1.5 py-0.5 bg-[#00A355]/15 text-[#00A355] border border-[#00A355]/40">
                      stock restored
                    </span>
                  )}
                </div>
                <div className="mt-1 text-[11px] sm:text-xs text-neutral-700 space-y-0.5">
                  <div>
                    Deleted by <b>{adminMap[o.deleted_by] || o.deleted_by || "unknown"}</b> on {o.deleted_at ? new Date(o.deleted_at).toLocaleString() : "?"}
                  </div>
                  <div className="text-neutral-500">
                    Customer: {o.user_id} · Placed: {new Date(o.created_at).toLocaleDateString()} · Total: <b className="text-black">{fmt(o.total)}</b>
                  </div>
                  {o.tm_ref && (
                    <div className="text-neutral-500">
                      Affiliate: <code className="font-mono">{o.tm_ref}</code>
                    </div>
                  )}
                </div>
                <div className="mt-2 pt-2 border-t border-[#FF2A00]/30 grid sm:grid-cols-2 gap-x-4 gap-y-0.5">
                  {(o.items || []).slice(0, 6).map((it, idx) => (
                    <div key={`${it.product_id}-${idx}`} className="flex justify-between text-[11px] sm:text-xs gap-2">
                      <span className="truncate">{it.title} × {it.quantity}</span>
                      <span className="font-semibold shrink-0">{fmt(it.price * it.quantity)}</span>
                    </div>
                  ))}
                  {(o.items || []).length > 6 && (
                    <div className="text-[11px] text-neutral-500 italic">+ {o.items.length - 6} more items</div>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => restore(o)}
                data-testid={`deleted-restore-${o.order_id}`}
                className="shrink-0 h-10 px-4 bg-[#0033FF] hover:bg-[#0022CC] text-white text-xs font-bold uppercase tracking-widest inline-flex items-center gap-1.5"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Restore
              </button>
            </div>
          </div>
        ))}
      </div>

      {orders.length > 50 && (
        <div className="mt-4 flex items-start gap-1.5 text-[11px] text-[#B25E00] bg-[#FFF7E6] border border-[#FFCC66] px-3 py-2">
          <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" />
          <span>Showing the 500 most recent deletions. Older tombstones are still in the database; ping support to bulk-purge if you need to.</span>
        </div>
      )}
    </div>
  );
}
